import { z } from 'zod';

import { isRepresentablePaise, paiseFromRupees, RUPEES_SHAPE } from './money';

/**
 * The field rules more than one form needs, each stated exactly once.
 *
 * **This file exists because a rule with two copies is a rule that eventually
 * disagrees with itself.** `PIN_MALFORMED` was written twice — in
 * `ChangeCredential.tsx` and in `routes/users/user-roles.ts` — by two workers
 * who could not see each other's branches, and nothing in `pnpm validate` could
 * have caught it: both copies were correct, and identical, and there was no
 * mechanism that would notice when one changed. A PIN the create form accepts
 * and the change-credential form refuses would have surfaced as somebody unable
 * to set the PIN they had just been issued.
 *
 * Every fragment here is **a courtesy and not the rule**. The API validates all
 * of it and the API is what decides; what this buys is a sentence in front of
 * somebody instead of a round trip that comes back as a generic refusal. Where
 * the published contract states no bound — a password's length, notably — there
 * is deliberately nothing here, because inventing one would refuse a value the
 * API would have accepted, which is a worse failure than the round trip.
 *
 * The messages are this app's own and are not `messageForFailure`'s. A server
 * refusal is worded from its `code` and only from its `code`; these are what a
 * form says before there is a server answer to word.
 */

/* -------------------------------------------------------------------------- */
/* Terminal PIN                                                                */
/* -------------------------------------------------------------------------- */

/**
 * 4 to 6 digits, stated by the API's own field description on `CreateUserDto`,
 * `UpdateUserDto` and `ChangeCredentialDto` alike.
 *
 * **The bounds are named rather than written into the pattern**, because the
 * upper one is now needed as a number as well as as a shape: the till's keypad
 * has to stop somewhere, and the field it fills has to stop in the same place.
 * A `maxLength` of 6 beside a `\d{4,6}` is two copies of one contract, which is
 * the failure this whole file exists to prevent.
 */
const PIN_MIN_DIGITS = 4;

/** The longest a PIN is, for anything that has to count digits rather than test them. */
export const PIN_MAX_DIGITS = 6;

const PIN_SHAPE = new RegExp(`^\\d{${String(PIN_MIN_DIGITS)},${String(PIN_MAX_DIGITS)}}$`);

export const PIN_MALFORMED = `A PIN is ${String(PIN_MIN_DIGITS)} to ${String(PIN_MAX_DIGITS)} digits, and digits only.`;

/**
 * A PIN somebody is setting.
 *
 * Not `.optional()` here: every form that sets a PIN sets it alongside a
 * "leave blank to change nothing" affordance, so the empty string is a real and
 * meaningful value on all three of them. `optionalPin` is that shape, and this
 * is the shape for the day a form makes a PIN mandatory.
 */
export const pinSchema = z.string().regex(PIN_SHAPE, PIN_MALFORMED);

/** A PIN that may be left blank, which is how "keep the one they have" is said. */
export const optionalPinSchema = z.union([z.literal(''), pinSchema]);

/* -------------------------------------------------------------------------- */
/* Staff code                                                                  */
/* -------------------------------------------------------------------------- */

/**
 * Letters, digits, and the separators a park puts on a badge — two to
 * thirty-two characters, starting with a letter or a digit.
 *
 * Restated from the server's `@Matches` because there is nothing to import: the
 * published document carries no `pattern` for this field.
 */
const STAFF_CODE_SHAPE = /^[A-Za-z0-9][A-Za-z0-9._-]{1,31}$/;

export const STAFF_CODE_REQUIRED = 'Enter a staff code for this person.';
export const STAFF_CODE_MALFORMED =
  'A staff code is 2 to 32 letters, digits, dots, dashes or underscores, starting with a letter or a digit.';

/**
 * A staff code being chosen for somebody.
 *
 * Trimmed before it is judged, so a pasted trailing space is a space and not a
 * malformed code — and so the value the form submits is the value it validated.
 */
export const staffCodeSchema = z
  .string()
  .trim()
  .min(1, STAFF_CODE_REQUIRED)
  .regex(STAFF_CODE_SHAPE, STAFF_CODE_MALFORMED);

/* -------------------------------------------------------------------------- */
/* Outlet code                                                                 */
/* -------------------------------------------------------------------------- */

export const OUTLET_CODE_REQUIRED = 'Enter a code for this outlet.';

/**
 * An outlet code.
 *
 * **Shape-checked for presence and nothing else, on purpose.** The published
 * contract states no pattern and no length for `code`, so the only thing this
 * can honestly refuse is an empty one. The hint under the field says short and
 * unique, and the placeholder shows what short looks like; a regular expression
 * here would be this app inventing a rule and then enforcing it against an API
 * that does not hold it.
 */
export const outletCodeSchema = z.string().trim().min(1, OUTLET_CODE_REQUIRED);

/* -------------------------------------------------------------------------- */
/* Catalogue item code and name                                                */
/* -------------------------------------------------------------------------- */

/**
 * Letters, digits, dashes and underscores, starting with a letter or a digit.
 *
 * Restated from the server's `@Matches(CATALOGUE_CODE_PATTERN)` for the same
 * reason `STAFF_CODE_SHAPE` is: there is nothing to import, because the
 * published document carries the `maxLength` but no `pattern`. The alternative
 * is a round trip that comes back `VALIDATION_FAILED` — a code with no message
 * of its own — for somebody who typed a space.
 *
 * `maxLength: 32` **is** published, so the length half of this is the contract
 * rather than a restatement.
 */
const CATALOGUE_CODE_SHAPE = /^[A-Za-z0-9][A-Za-z0-9_-]*$/;
const CATALOGUE_CODE_MAX = 32;

export const CATALOGUE_CODE_REQUIRED = 'Enter a code for this item.';
export const CATALOGUE_CODE_MALFORMED =
  'A code is letters, digits, dashes and underscores, starting with a letter or a digit.';
export const CATALOGUE_CODE_TOO_LONG = `A code is at most ${String(CATALOGUE_CODE_MAX)} characters.`;

/**
 * A catalogue item's code.
 *
 * Unlike an outlet code this one is **never reissued** — retiring an item does
 * not free it, because it is on receipts and tickets that outlive the record —
 * so the copy around this field says so and `CATALOGUE_CODE_TAKEN` is worded as
 * spent rather than as in use. That distinction is in `error-messages.ts`; what
 * lives here is only the shape.
 */
export const catalogueCodeSchema = z
  .string()
  .trim()
  .min(1, CATALOGUE_CODE_REQUIRED)
  .max(CATALOGUE_CODE_MAX, CATALOGUE_CODE_TOO_LONG)
  .regex(CATALOGUE_CODE_SHAPE, CATALOGUE_CODE_MALFORMED);

/** `maxLength: 120` on `CreateCatalogueItemDto.name`, published. */
const CATALOGUE_NAME_MAX = 120;

export const CATALOGUE_NAME_REQUIRED = 'Enter a name for this item.';
export const CATALOGUE_NAME_TOO_LONG = `A name is at most ${String(CATALOGUE_NAME_MAX)} characters.`;

export const catalogueNameSchema = z
  .string()
  .trim()
  .min(1, CATALOGUE_NAME_REQUIRED)
  .max(CATALOGUE_NAME_MAX, CATALOGUE_NAME_TOO_LONG);

/* -------------------------------------------------------------------------- */
/* Why a ticket was voided                                                     */
/* -------------------------------------------------------------------------- */

/**
 * 280 characters, published on both void routes — `VALIDATION_FAILED` for "a
 * reason longer than 280 characters" — so the length is the contract rather than
 * a restatement.
 */
const VOID_REASON_MAX = 280;

export const VOID_REASON_REQUIRED = 'Say why this ticket is being voided.';
export const VOID_REASON_TOO_LONG = `A reason is at most ${String(VOID_REASON_MAX)} characters.`;

/**
 * Why a ticket was voided — asked, and insisted on, by the back office, which is
 * the only screen that voids one (#285). **The API makes `reason` optional on
 * both routes**; insisting is the screen's decision, not contract. Trimmed before
 * it is judged and before it is sent.
 */
export const requiredVoidReasonSchema = z
  .string()
  .trim()
  .min(1, VOID_REASON_REQUIRED)
  .max(VOID_REASON_MAX, VOID_REASON_TOO_LONG);

/* -------------------------------------------------------------------------- */
/* Why a sale was refunded                                                     */
/* -------------------------------------------------------------------------- */

/**
 * 280 characters, published on both refund routes — `VALIDATION_FAILED` for "a
 * reason longer than 280 characters" — so the length is the contract rather than
 * a restatement.
 *
 * Stated again rather than shared with the void bound above, which happens to be
 * the same number today. They are two contracts on four different routes, and a
 * shared constant would mean a change to one silently moving the other.
 */
const REFUND_REASON_MAX = 280;

export const REFUND_REASON_REQUIRED = 'Say why this sale is being refunded.';
export const REFUND_REASON_TOO_LONG = `A reason is at most ${String(REFUND_REASON_MAX)} characters.`;

/**
 * Why a sale was refunded, where the screen does not insist on one.
 *
 * **The API makes `reason` optional on both refund routes**, and neither shape
 * here changes that — what differs is whether a given screen asks. The till does
 * not insist and the back office does, and both arguments are made where the
 * decision lives rather than here.
 *
 * Trimmed before it is judged and before it is sent, so a field holding only
 * spaces is the same thing as an empty one. `''` is a real value: it is how
 * "there is no reason" is said, and the caller drops the field rather than
 * sending a blank one — a blank reason reads afterwards as a reason that was
 * given.
 */
export const refundReasonSchema = z.union([
  z.literal(''),
  z.string().trim().max(REFUND_REASON_MAX, REFUND_REASON_TOO_LONG),
]);

/** The same field where a screen asks for one. Never a stricter *shape*. */
export const requiredRefundReasonSchema = z
  .string()
  .trim()
  .min(1, REFUND_REASON_REQUIRED)
  .max(REFUND_REASON_MAX, REFUND_REASON_TOO_LONG);

/* -------------------------------------------------------------------------- */
/* Why an imported machine bill was reversed                                   */
/* -------------------------------------------------------------------------- */

/**
 * 1–500 characters after trimming, required — `ReverseMachineBillDto`'s own
 * bound, refused as `MACHINE_BILL_REVERSAL_REASON_INVALID` otherwise. Its own
 * constant for the reason the refund bound above gives.
 */
const MACHINE_BILL_REVERSAL_REASON_MAX = 500;

export const MACHINE_BILL_REVERSAL_REASON_REQUIRED =
  'Say why this bill should not have been imported.';
export const MACHINE_BILL_REVERSAL_REASON_TOO_LONG = `A reason is at most ${String(MACHINE_BILL_REVERSAL_REASON_MAX)} characters.`;

export const machineBillReversalReasonSchema = z
  .string()
  .trim()
  .min(1, MACHINE_BILL_REVERSAL_REASON_REQUIRED)
  .max(MACHINE_BILL_REVERSAL_REASON_MAX, MACHINE_BILL_REVERSAL_REASON_TOO_LONG);

/* -------------------------------------------------------------------------- */
/* A billing machine's name                                                    */
/* -------------------------------------------------------------------------- */

/**
 * 1–100 characters after trimming — the bound `RegisterBillingMachineDto` and
 * `RenameBillingMachineDto` both hold. Registering and renaming share it.
 */
const MACHINE_NAME_MAX = 100;

export const MACHINE_NAME_REQUIRED = 'Enter a name for this machine.';
export const MACHINE_NAME_TOO_LONG = `A name is at most ${String(MACHINE_NAME_MAX)} characters.`;

export const machineNameSchema = z
  .string()
  .trim()
  .min(1, MACHINE_NAME_REQUIRED)
  .max(MACHINE_NAME_MAX, MACHINE_NAME_TOO_LONG);

/* -------------------------------------------------------------------------- */
/* A price, typed in rupees                                                    */
/* -------------------------------------------------------------------------- */

export const PRICE_REQUIRED = 'Enter an amount in rupees. Zero is allowed.';
export const PRICE_MALFORMED =
  'An amount is rupees and paise — 25, 25.50, or 0. No symbol, and no more than two decimal places.';
export const PRICE_UNREPRESENTABLE =
  'That amount is larger than this screen can carry without rounding it. Check the figure.';

/**
 * What somebody types into a price field, before `paiseFromRupees` converts it.
 *
 * **Zero is valid and nothing here says otherwise.** A free child ticket is
 * `0`, and a selling price below cost is a loss leader the API accepts on
 * purpose (FR-205) — so there is no minimum, no "must be more than cost", and
 * no rounding to a whole rupee. `docs/ui-conventions.md` and this ticket both
 * ask that the client not invent a rule the API does not hold, and a price
 * field is where the temptation is strongest.
 *
 * A string rather than a `z.number()`: `valueAsNumber` on an empty input is
 * `NaN`, and the resolver then has to tell an untouched field apart from a
 * malformed one. Held as text, "typed nothing" and "typed nonsense" are two
 * different messages, which is what a person needs.
 */
export const priceInRupeesSchema = z
  .string()
  .trim()
  .min(1, PRICE_REQUIRED)
  .regex(RUPEES_SHAPE, PRICE_MALFORMED)
  .refine((rupees) => isRepresentablePaise(paiseFromRupees(rupees)), PRICE_UNREPRESENTABLE);

/* -------------------------------------------------------------------------- */
/* GSTIN                                                                       */
/* -------------------------------------------------------------------------- */

/**
 * The Indian GSTIN shape, as the API checks it after upper-casing — one
 * pattern for a vendor's and for the park's own (`GSTIN_PATTERN` in its
 * `vendors.service.ts`, imported by the park identity's service).
 */
const GSTIN_SHAPE = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/;

export const GSTIN_MALFORMED = 'A GSTIN is 15 characters, like 29ABCDE1234F1Z5.';

/** A GSTIN that may be left blank, which is how "not set" is said. Upper-cased, as stored. */
export const optionalGstinSchema = z
  .string()
  .trim()
  .toUpperCase()
  .refine((value) => value === '' || GSTIN_SHAPE.test(value), GSTIN_MALFORMED);

/* -------------------------------------------------------------------------- */
/* Barcode                                                                     */
/* -------------------------------------------------------------------------- */

/**
 * 8, 12, 13 or 14 digits — EAN-8, UPC-A, EAN-13 and GTIN-14 (#319).
 *
 * The API enforces it in the `catalogue_items_barcode_shape` CHECK and in the
 * DTO, but states it only in a field description, with no `pattern` in the
 * schema, so it is written out here. If the API widens it, this must follow.
 */
const BARCODE_SHAPE = /^(?:\d{8}|\d{12,14})$/;

export const BARCODE_MALFORMED = 'A barcode is 8, 12, 13 or 14 digits, and digits only.';

/** Whether a value could be a barcode — what the till asks of an Enter that matched nothing. */
export function isBarcodeShaped(value: string): boolean {
  return BARCODE_SHAPE.test(value);
}

/** A barcode that may be left blank, which is how "it has none" is said. */
export const optionalBarcodeSchema = z
  .string()
  .trim()
  .refine((value) => value === '' || isBarcodeShaped(value), BARCODE_MALFORMED);

/**
 * The barcode as the API takes it. **Blank is `null`, never `""`**: the API
 * reads `null` as "it has none" and refuses an empty string.
 */
export function barcodeOnTheWire(value: string): string | null {
  const trimmed = value.trim();

  return trimmed === '' ? null : trimmed;
}
